import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const TRUCK_TYPES = ["Dry Van", "Reefer", "Flatbed", "Step Deck", "Power Only", "Hotshot", "Conestoga", "Other"];
const FLEET_SIZES = ["1 truck", "2–5 trucks", "6–15 trucks", "16+ trucks"];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "",
    mc_number: "", dot_number: "",
    truck_type: "Dry Van", home_base: "", fleet_size: "1 truck",
    preferred_lanes: "", notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/dispatch-requests", form);
      setDone(data);
      toast.success("Signup received. A dispatcher will reach out within one business hour.");
      setForm({ name: "", email: "", phone: "", company: "", mc_number: "", dot_number: "", truck_type: "Dry Van", home_base: "", fleet_size: "1 truck", preferred_lanes: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed to submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const baseInput = "w-full bg-white border border-black/15 px-4 py-3 text-sm focus:outline-none focus:border-[#FF4500] transition-colors";

  return (
    <section id="quote" className="bg-[#F1F3F5] py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
        <Reveal className="md:col-span-5">
          <p className="font-mono-label text-xs text-[#FF4500]">CHAPTER 05 / CARRIER SIGN UP</p>
          <h2 className="font-display font-bold text-4xl md:text-6xl mt-4 leading-[1]">
            Get your<br/>truck <span className="italic">dispatched.</span>
          </h2>
          <p className="mt-6 text-[#495057] leading-relaxed max-w-md">
            Tell us about your truck and lanes. Most carriers are set up under our authority and running loads within 24 hours.
          </p>
          <div className="mt-10 border-t border-black/10 pt-8">
            <p className="font-mono-label text-xs text-black/50">Typical response</p>
            <p className="font-display font-bold text-6xl mt-2">&lt; 1<span className="text-[#FF4500]">hr</span></p>
            <p className="text-xs text-black/50 mt-2 font-mono-label">MON–FRI 8:00–5:00 CT</p>
          </div>
        </Reveal>

        <motion.div
          initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-10% 0px" }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="md:col-span-7">
          {done ? (
            <div className="bg-white border border-[#FF4500] p-10" data-testid="quote-success-card">
              <p className="font-mono-label text-xs text-[#FF4500]">SIGNUP #{done.id.slice(0, 8).toUpperCase()}</p>
              <h3 className="font-display font-bold text-3xl mt-3">You&apos;re on the list.</h3>
              <p className="text-[#495057] mt-3 leading-relaxed">
                A dispatcher will reach out at <strong>{done.email}</strong> or call <strong>{done.phone}</strong> within one business hour to finish onboarding.
              </p>
              <button onClick={() => setDone(null)} className="sharp-btn mt-6 border border-black px-5 py-3 text-sm font-medium hover:bg-black hover:text-white" data-testid="quote-new-button">
                Sign up another truck
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-black/10 p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="quote-form">
              <div>
                <label className="font-mono-label text-[10px] text-black/50">FULL NAME</label>
                <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={baseInput} data-testid="quote-input-name"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">COMPANY (LLC)</label>
                <input value={form.company} onChange={(e) => set("company", e.target.value)} className={baseInput} data-testid="quote-input-company"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">EMAIL</label>
                <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={baseInput} data-testid="quote-input-email"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">PHONE</label>
                <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={baseInput} data-testid="quote-input-phone"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">MC NUMBER</label>
                <input value={form.mc_number} onChange={(e) => set("mc_number", e.target.value)} placeholder="e.g. 1234567" className={baseInput} data-testid="quote-input-mc"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">DOT NUMBER</label>
                <input value={form.dot_number} onChange={(e) => set("dot_number", e.target.value)} placeholder="e.g. 3456789" className={baseInput} data-testid="quote-input-dot"/>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">TRUCK TYPE</label>
                <select value={form.truck_type} onChange={(e) => set("truck_type", e.target.value)} className={baseInput} data-testid="quote-input-truck">
                  {TRUCK_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono-label text-[10px] text-black/50">FLEET SIZE</label>
                <select value={form.fleet_size} onChange={(e) => set("fleet_size", e.target.value)} className={baseInput}>
                  {FLEET_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-label text-[10px] text-black/50">HOME BASE (CITY, STATE)</label>
                <input value={form.home_base} onChange={(e) => set("home_base", e.target.value)} placeholder="e.g. Dallas, TX" className={baseInput} data-testid="quote-input-home"/>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-label text-[10px] text-black/50">PREFERRED LANES</label>
                <input value={form.preferred_lanes} onChange={(e) => set("preferred_lanes", e.target.value)} placeholder="e.g. TX ⇄ CA, Midwest, home-weekly" className={baseInput}/>
              </div>
              <div className="md:col-span-2">
                <label className="font-mono-label text-[10px] text-black/50">NOTES</label>
                <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Home time needs, factoring company, current rate target…" className={baseInput}/>
              </div>
              <button type="submit" disabled={submitting} className="md:col-span-2 sharp-btn bg-black text-white py-4 font-medium text-sm hover:bg-[#FF4500] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="quote-submit-button">
                {submitting ? <><Loader2 size={16} className="animate-spin"/> Submitting…</> : <>Sign My Truck Up <ArrowRight size={16}/></>}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
